/**
 * WorkflowResultSection — renders the persisted result artifact for a
 * completed workflow instance.
 *
 * Pure presentation — no fetch. Each host app owns its own fetch because
 * the transports differ (ora: Remix resource route with credentials cookie;
 * plugin: orchestratorApi stub-able client). Pass the resolved result body
 * once you have it.
 *
 * Import alias in ora:     ~/common/workflows/components/WorkflowResultSection
 * Import alias in add-in:  @product-app/common/workflows/components/WorkflowResultSection
 */
import { useMemo } from "react";
import type { WorkflowResultBody } from "../result.types";
import { isWarningKey } from "../resultWarnings";
import { isTruncatableBlob, pruneResultKeys } from "../resultFilters";
import { FormattedJsonView } from "./FormattedJsonView";

export type { WorkflowResultBody };

export interface WorkflowResultSectionProps {
  result: WorkflowResultBody | null;
  loading?: boolean;
  /**
   * When true the component falls back to a JSON dump of the full result
   * body when no `artifact` field is present. This is now the ONLY rendering
   * of a terminal run's fields in either host — `FinalStateCard` used to sit
   * above it, formatting `status.outputs`, but nothing has ever emitted that
   * key so the card never rendered and it has been removed.
   */
  jsonFallback?: boolean;
}

export function WorkflowResultSection({
  result,
  loading = false,
  jsonFallback = false,
}: WorkflowResultSectionProps) {
  // Build the JSON-tree data once per result: strip skip-keys (login
  // screenshots, api evidence steps, captured review steps, steps reviewed, the
  // Graph token) recursively, then drop ``*_warning`` keys (surfaced as a banner
  // elsewhere). Memoised so we don't deep-copy the (large) tree every render.
  // Declared before the early returns so the hook order stays stable.
  const treeData = useMemo<Record<string, unknown> | null>(() => {
    if (!result) return null;
    const pruned = pruneResultKeys(result) as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(pruned)) {
      if (!isWarningKey(k)) out[k] = v;
    }
    return out;
  }, [result]);

  if (loading) {
    return (
      <div
        className="h-10 rounded-md bg-primary-gray-light-100 animate-pulse mt-2"
        aria-label="Result (loading)"
        data-testid="workflow-result-loading"
      />
    );
  }

  if (!result) return null;

  // Pre-formatted artifact string (e.g. a markdown summary) → keep raw <pre>.
  if (typeof result.artifact === "string" && result.artifact.length > 0) {
    return (
      <pre
        className="mt-2 text-xs font-mono bg-primary-gray-light-50 rounded-xl p-3 overflow-auto whitespace-pre-wrap text-primary-gray-light-800 border border-primary-gray-light-200"
        data-testid="workflow-result-artifact"
      >
        {result.artifact}
      </pre>
    );
  }

  // Structured object → render via FormattedJsonView so keys are humanised
  // (`mortgagees_applied` → "Mortgagees Applied") and nested values indent.
  // `treeData` is the pruned, warning-filtered copy built above. Long base64
  // blobs (attachment/document bytes, the original .eml, signature images)
  // collapse behind a "Show more" toggle via `isTruncatableBlob`.
  if (jsonFallback && treeData && Object.keys(treeData).length > 0) {
    return (
      <div className="mt-2" data-testid="workflow-result-artifact">
        <FormattedJsonView data={treeData} shouldTruncate={isTruncatableBlob} />
      </div>
    );
  }

  return null;
}
